import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
  FeatureResource,
  VehicleRequirement,
  featureGet,
  featurePut,
} from "../../api/vehicleFeatures";
import { EntityFormDialog } from "../Dialog/EntityFormDialog";
import { Button } from "../Button/Button";
import { RequirementFields } from "../VehicleFeatures/FeatureFields";
import { orderRequirementsKey } from "../VehicleFeatures/useOrderRequirements";

export function OrderRequirementsDialog({
  workspaceId,
  orderId,
  onClose,
}: {
  workspaceId: string;
  orderId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();
  const [snapshot, setSnapshot] = useState<FeatureResource>();
  const [requirements, setRequirements] = useState<VehicleRequirement[]>([]);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const saving = useRef(false);
  useEffect(() => {
    const request = ++generation.current;
    setSnapshot(undefined);
    setError(false);
    featureGet<FeatureResource>(workspaceId, `order/${orderId}`)
      .then((data) => {
        if (generation.current !== request) return;
        setSnapshot(data);
        setRequirements(data.requirements || []);
      })
      .catch(() => {
        if (generation.current === request) setError(true);
      });
    return () => {
      generation.current++;
    };
  }, [workspaceId, orderId, reload]);
  const changed =
    snapshot &&
    JSON.stringify(requirements) !==
      JSON.stringify(snapshot.requirements || []);
  const missingValue = requirements.some(
    (requirement) => requirement.value === "",
  );
  const save = async () => {
    if (!snapshot?.editable || !changed || missingValue || saving.current)
      return;
    const request = generation.current;
    saving.current = true;
    setBusy(true);
    setError(false);
    try {
      const saved = await featurePut<FeatureResource>(
        workspaceId,
        `order/${orderId}`,
        {
          expectedRevision: snapshot.revision,
          schemaRevision: snapshot.schema.revision,
          requirements,
        },
      );
      // A focus refresh started before this save must not overwrite its result.
      await queryClient.cancelQueries(
        orderRequirementsKey(workspaceId, orderId),
      );
      queryClient.setQueryData(
        orderRequirementsKey(workspaceId, orderId),
        saved,
      );
      void queryClient.invalidateQueries([
        "planner-vehicle-assessment",
        workspaceId,
        orderId,
      ]);
      if (generation.current === request) onClose();
    } catch {
      if (generation.current === request) setError(true);
    } finally {
      saving.current = false;
      if (generation.current === request) setBusy(false);
    }
  };
  return (
    <EntityFormDialog
      title={t("features.editRequirements")}
      show
      onCancel={() => {
        if (!saving.current) onClose();
      }}
    >
      <form
        className="text-text"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {!snapshot && !error && (
            <p role="status" className="py-6 text-sm text-secondary">
              {t("features.loading")}
            </p>
          )}
          {snapshot && (
            <RequirementFields
              definitions={snapshot.schema.definitions}
              value={requirements}
              onChange={setRequirements}
              disabled={busy || !snapshot.editable}
            />
          )}
          {snapshot && !snapshot.editable && (
            <p className="mt-3 text-sm text-secondary">
              {t("features.requirementsLocked")}
            </p>
          )}
          {missingValue && snapshot && (
            <p className="mt-3 text-sm text-danger">
              {t("features.missingValue")}
            </p>
          )}
          {error && (
            <p role="alert" className="mt-3 text-sm text-danger">
              {t("features.error")}{" "}
              <button
                type="button"
                disabled={busy}
                className="underline"
                onClick={() => setReload((value) => value + 1)}
              >
                {t("features.reload")}
              </button>
            </p>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3 border-t border-border pt-4">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={onClose}
          >
            {t("features.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={busy || !snapshot?.editable || !changed || missingValue}
          >
            {t(busy ? "features.saving" : "features.saveRequirements")}
          </Button>
        </div>
      </form>
    </EntityFormDialog>
  );
}
